/**
 * Shown inside the portal when an adviser tries to take on a client but the firm is out of paid
 * capacity. What it offers depends on the server's `summary.needs`:
 *   'partner_client'  — pay-as-you-go firm: $336 for this client, or switch to the annual plan.
 *   'partner_overage' — annual firm past its included 20: $70 for this extra client.
 * Every button routes to Stripe hosted Checkout; on return the adviser adds the client again with
 * capacity available. Only the firm owner will succeed (the server enforces owner + aal2).
 */
import React, { useState } from 'react';
import { X, UserPlus, CalendarClock, ArrowRight, ShieldCheck, AlertCircle } from 'lucide-react';
import { startCheckout, type CheckoutPlan, type PartnerBillingSummary } from '../../services/billingApi.ts';

interface AddClientPaywallProps {
  summary?: PartnerBillingSummary | null;
  tenantId?: string | null;
  onClose: () => void;
}

const AddClientPaywall = ({ summary, tenantId, onClose }: AddClientPaywallProps) => {
  const [busy, setBusy] = useState<CheckoutPlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const needs = summary?.needs || 'partner_client';
  const isOverage = needs === 'partner_overage';

  const go = async (plan: CheckoutPlan) => {
    setBusy(plan);
    setError(null);
    try {
      const res = await startCheckout(plan, tenantId);
      if (!res.ok) {
        setError(res.message || 'Could not start the payment page. Please try again.');
        setBusy(null);
      }
    } catch {
      setError('We couldn’t reach the payment service. Please try again in a moment.');
      setBusy(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-slate-100 p-5">
          <div>
            <h2 className="text-lg font-bold text-slate-900">
              {isOverage ? 'One more client' : 'Add another client'}
            </h2>
            <p className="mt-0.5 text-sm text-slate-500">
              {isOverage
                ? 'Your annual plan includes 20 clients — this one is extra.'
                : 'You’ve used the clients you’ve paid for so far.'}
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600">
            <X size={18} />
          </button>
        </div>

        <div className="space-y-3 p-5">
          {isOverage ? (
            <button
              type="button"
              onClick={() => go('partner_overage')}
              disabled={busy !== null}
              className="flex w-full items-center justify-between gap-2 rounded-xl bg-gradient-to-r from-[#1a6fd4] to-[#17b8e0] px-4 py-3.5 text-left font-semibold text-white shadow-lg shadow-blue-600/25 transition hover:brightness-[1.04] disabled:opacity-50"
            >
              <span className="flex items-center gap-2"><UserPlus size={17} /> Add this client — $70 + GST</span>
              {busy === 'partner_overage' ? <span className="text-sm">One moment…</span> : <ArrowRight size={16} />}
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => go('partner_client')}
                disabled={busy !== null}
                className="flex w-full items-center justify-between gap-2 rounded-xl bg-gradient-to-r from-[#1a6fd4] to-[#17b8e0] px-4 py-3.5 text-left font-semibold text-white shadow-lg shadow-blue-600/25 transition hover:brightness-[1.04] disabled:opacity-50"
              >
                <span className="flex items-center gap-2"><UserPlus size={17} /> Add this client — $336 + GST</span>
                {busy === 'partner_client' ? <span className="text-sm">One moment…</span> : <ArrowRight size={16} />}
              </button>
              <button
                type="button"
                onClick={() => go('partner')}
                disabled={busy !== null}
                className="flex w-full items-center justify-between gap-2 rounded-xl border-2 border-[#1a6fd4] px-4 py-3.5 text-left font-semibold text-[#1a6fd4] transition hover:bg-blue-50 disabled:opacity-50"
              >
                <span className="flex items-center gap-2"><CalendarClock size={17} /> Switch to annual — $800 + GST/yr</span>
                {busy === 'partner' ? <span className="text-sm">One moment…</span> : <ArrowRight size={16} />}
              </button>
              <p className="text-center text-xs text-slate-400">The annual plan includes 20 clients, then $70 each.</p>
            </>
          )}

          {error && (
            <div className="flex items-start gap-2.5 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
              <AlertCircle size={16} className="mt-0.5 shrink-0 text-red-600" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="flex items-center justify-center gap-1.5 pt-1 text-xs text-slate-400">
            <ShieldCheck size={13} /> Secure payment by Stripe
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddClientPaywall;
